// Admin Analytics Sanity Page
// Shows the last 50 analytics events for quick debugging
// Filters by event type, user ID

import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { 
  ArrowLeft, 
  Activity,
  Filter,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Clock,
  User,
  Search,
  Loader2
} from "lucide-react";
import { AuthContext } from "../App";
import apiClient from "../api/client";
import { toast } from "sonner";
import { GlobalNav } from "../components/GlobalNav";


// Event type colors for visual distinction
const EVENT_TYPE_COLORS = {
  auth: "text-blue-400 bg-blue-500/10",
  checkout: "text-green-400 bg-green-500/10",
  discovery: "text-purple-400 bg-purple-500/10",
  messaging: "text-cyan-400 bg-cyan-500/10",
  her_space: "text-rose-400 bg-rose-500/10",
  couples: "text-pink-400 bg-pink-500/10",
  error: "text-red-400 bg-red-500/10",
  denied: "text-orange-400 bg-orange-500/10",
  default: "text-gray-400 bg-gray-500/10",
};

function getEventColor(eventType) {
  if (!eventType) return EVENT_TYPE_COLORS.default;
  
  const lower = eventType.toLowerCase();
  if (lower.includes("error") || lower.includes("failed")) return EVENT_TYPE_COLORS.error;
  if (lower.includes("denied") || lower.includes("rate_limit")) return EVENT_TYPE_COLORS.denied;
  if (lower.includes("login") || lower.includes("auth")) return EVENT_TYPE_COLORS.auth;
  if (lower.includes("checkout") || lower.includes("purchase")) return EVENT_TYPE_COLORS.checkout;
  if (lower.includes("discover") || lower.includes("match")) return EVENT_TYPE_COLORS.discovery;
  if (lower.includes("message") || lower.includes("chat")) return EVENT_TYPE_COLORS.messaging;
  if (lower.includes("her_space")) return EVENT_TYPE_COLORS.her_space;
  if (lower.includes("couple")) return EVENT_TYPE_COLORS.couples;
  
  return EVENT_TYPE_COLORS.default;
}

function formatTimestamp(ts) {
  if (!ts) return "N/A";
  try {
    const d = new Date(ts);
    return d.toLocaleString();
  } catch {
    return ts;
  }
}

export default function AdminAnalyticsSanityPage() {
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [events, setEvents] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [summary, setSummary] = useState(null);
  
  // Filters
  const [filterType, setFilterType] = useState("");
  const [filterUserId, setFilterUserId] = useState("");
  const [limit, setLimit] = useState(50);
  
  // Fetch events
  const fetchEvents = async (showRefresh = false) => {
    try {
      if (showRefresh) setRefreshing(true);
      else setLoading(true);
      
      const params = new URLSearchParams();
      params.append("limit", limit.toString());
      if (filterType) params.append("event_type", filterType);
      if (filterUserId) params.append("user_id", filterUserId);
      
      const [eventsRes, summaryRes, typesRes] = await Promise.all([
        apiClient.get(`/admin/analytics-sanity/events?${params}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        apiClient.get(`/admin/analytics-sanity/summary?hours=24`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        apiClient.get(`/admin/analytics-sanity/event-types`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
      ]);
      
      setEvents(eventsRes.data.events || []);
      setSummary(summaryRes.data);
      setEventTypes(typesRes.data.all_types || []);
    } catch (err) {
      console.error("Failed to fetch analytics:", err);
      if (err.response?.status === 403) {
        toast.error("Admin access required");
        navigate("/dashboard");
      } else {
        toast.error("Failed to load analytics");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  
  useEffect(() => {
    if (token) {
      fetchEvents();
    }
  }, [token]); // fetchEvents is stable, no need to include
  
  // Apply filters
  const handleApplyFilters = () => {
    fetchEvents();
  };
  
  // Clear filters
  const handleClearFilters = () => {
    setFilterType("");
    setFilterUserId("");
    setLimit(50);
    fetchEvents();
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-gray-900">
        <GlobalNav />
        <div className="flex items-center justify-center py-32">
          <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-gray-900">
      <GlobalNav />
      
      <div className="max-w-6xl mx-auto px-4 py-6 pb-24">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 transition"
            >
              <ArrowLeft className="w-5 h-5 text-gray-300" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                <Activity className="w-6 h-6 text-purple-400" />
                Analytics Sanity Check
              </h1>
              <p className="text-sm text-gray-400">
                Last {limit} events • Real-time debugging
              </p>
            </div>
          </div>
          <button
            onClick={() => fetchEvents(true)}
            disabled={refreshing}
            className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition flex items-center gap-2 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
        
        {/* Summary Card */}
        {summary && (
          <div className={`mb-6 rounded-xl p-6 border ${
            summary.status === "healthy" 
              ? "bg-green-900/20 border-green-500/30" 
              : "bg-yellow-900/20 border-yellow-500/30"
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {summary.status === "healthy" ? (
                  <CheckCircle className="w-6 h-6 text-green-400" />
                ) : (
                  <AlertTriangle className="w-6 h-6 text-yellow-400" />
                )}
                <div>
                  <h2 className="text-lg font-semibold text-white">
                    {summary.status === "healthy" ? "System Healthy" : "Attention Needed"}
                  </h2>
                  <p className="text-sm text-gray-400">
                    {summary.total_events} events in last {summary.period_hours} hours
                  </p>
                </div>
              </div>
              {summary.potential_issues?.length > 0 && (
                <div className="text-right">
                  <p className="text-sm text-yellow-400 font-medium">Issues detected:</p>
                  {summary.potential_issues.slice(0, 3).map((issue, i) => (
                    <p key={i} className="text-xs text-gray-400">{issue}</p>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Filters */}
        <div className="bg-gray-900/60 border border-gray-700 rounded-xl p-4 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-gray-400" />
            <span className="text-sm font-medium text-gray-300">Filters</span>
          </div>
          <div className="flex flex-wrap gap-3">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-sm text-white focus:border-purple-500 focus:outline-none"
            >
              <option value="">All event types</option>
              {eventTypes.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={filterUserId}
                onChange={(e) => setFilterUserId(e.target.value)}
                placeholder="User ID..."
                className="pl-9 pr-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-sm text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none w-48"
              />
            </div>
            <select
              value={limit}
              onChange={(e) => setLimit(parseInt(e.target.value))}
              className="px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-sm text-white focus:border-purple-500 focus:outline-none"
            >
              <option value={25}>25 events</option>
              <option value={50}>50 events</option>
              <option value={100}>100 events</option>
              <option value={200}>200 events</option>
            </select>
            <button
              onClick={handleApplyFilters}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm rounded-lg transition flex items-center gap-2"
            >
              <Search className="w-4 h-4" />
              Apply
            </button>
            {(filterType || filterUserId) && (
              <button
                onClick={handleClearFilters}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg transition"
              >
                Clear
              </button>
            )}
          </div>
        </div>
        
        {/* Events Table */}
        <div className="bg-gray-900/60 border border-gray-700 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700 text-left">
                  <th className="px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Timestamp
                  </th>
                  <th className="px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Event
                  </th>
                  <th className="px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">
                    User ID
                  </th>
                  <th className="px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Properties
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {events.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                      No events found matching your filters.
                    </td>
                  </tr>
                )}
                {events.map((event, index) => (
                  <tr key={index} className="hover:bg-gray-800/50">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2 text-sm text-gray-400">
                        <Clock className="w-3 h-3" />
                        {formatTimestamp(event.timestamp)}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getEventColor(event.event)}`}>
                        {event.event}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-sm text-gray-300 font-mono">
                        {event.user_id ? `${event.user_id.substring(0, 8)}...` : "-"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-xs text-gray-500 max-w-md truncate">
                        {event.properties ? (
                          <code className="bg-gray-800 px-2 py-1 rounded">
                            {JSON.stringify(event.properties).substring(0, 100)}
                            {JSON.stringify(event.properties).length > 100 && "..."}
                          </code>
                        ) : "-"}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Footer info */}
        <div className="mt-4 text-center text-xs text-gray-500">
          Showing {events.length} events • Auto-refresh disabled for performance
        </div>
      </div>
    </div>
  );
}
